import { useCallback, useEffect, useMemo, useRef } from 'react';

import Animated from 'react-native-reanimated';
import View = Animated.View;
import {
  Chip,
  Colors,
  Flex,
  List,
  Typography,
  useScreenSize,
  type ListRef,
} from '@banhala/ui-kit';
import type { RadioFilter, RadioFilterItem } from '../types';
import type { AnyObject } from '../../../../../types';
import { useFilterListAtom } from '../Provider/filterList/useFilterListAtom';
import { useLogClickFilter } from '../hooks/useLogClickFilter';

const AnimatedView = Animated.createAnimatedComponent(View);
const AnimatedList = Animated.createAnimatedComponent(List<RadioFilterItem>);

type HorizontalRadioListProps = {
  radioFilter: RadioFilter;
  logging: AnyObject;
  scrollToFilterList: VoidFunction;
};

export const HorizontalRadioList = ({
  radioFilter,
  logging,
  scrollToFilterList,
}: HorizontalRadioListProps) => {
  const { setRadioFilter } = useFilterListAtom();
  const logClickFilter = useLogClickFilter(logging);

  const isFirstRenderRef = useRef(true);
  const radioListRef = useRef<ListRef<RadioFilterItem>>(null);

  const { width: screenWidth } = useScreenSize();
  const scrollToItemTargetPosition = useMemo(() => {
    return screenWidth * 0.5 - 20;
  }, [screenWidth]);

  const selectedIndex = useMemo(() => {
    return radioFilter.possible_items.findIndex(
      item => item.query_value === radioFilter.selected_value,
    );
  }, [radioFilter]);

  const scrollToIndex = useCallback((index: number, animated: boolean) => {
      radioListRef.current?.scrollToIndex({
        index,
        animated,
        viewOffset: scrollToItemTargetPosition,
      });
    },
    [scrollToItemTargetPosition],
  );

  const scrollToSelectedItem = useCallback((animated: boolean) => {
      if (selectedIndex >= 0) {
        scrollToIndex(selectedIndex, animated);
      }
    },
    [selectedIndex, scrollToIndex],
  );

  const onScrollToIndexFailed = useCallback((info: { index: number }) => {
      requestAnimationFrame(() => {
        scrollToIndex(info.index, false);
      });
    },
    [scrollToIndex],
  );

  // 첫 렌더링 시 컨텐츠가 렌더링된 후 스크롤
  const onContentSizeChange = useCallback(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      scrollToSelectedItem(false);
    }
  }, [scrollToSelectedItem]);

  // 필터 변경 시 스크롤 (첫 렌더링 이후)
  useEffect(() => {
    if (!isFirstRenderRef.current) {
      scrollToSelectedItem(true);
    }
  }, [radioFilter, scrollToSelectedItem]);

  const handleChangeFilter = useCallback((item: RadioFilterItem) => {
      logClickFilter({ itemLogging: item.logging });
      scrollToFilterList();
      setRadioFilter({
        queryName: radioFilter.query_name,
        queryValue: item.query_value,
      });
    },
    [logClickFilter, scrollToFilterList, setRadioFilter, radioFilter.query_name],
  );

  const isSecondaryTab = radioFilter.view_type === 'SECONDARY_TAB';
  const isChip = radioFilter.view_type === 'CHIP';

  const contentContainerStyle = useMemo(
    () => ({
      paddingHorizontal: 16,
      gap: isChip ? 8 : isSecondaryTab ? 8 : 20,
      ...(isChip && { paddingVertical: 12 }),
    }),
    [isSecondaryTab, isChip],
  );

  const renderItem = ({ item, index }: { item: RadioFilterItem; index: number }) => {
    const { text, query_value } = item;
    const isSelected = query_value === radioFilter.selected_value;
    const testID = `FILTER_LIST?LIST_INDEX=${index}&VALUE=${text}`;

    if (isChip) {
      return (
        <Chip
          testID={testID}
          size="medium"
          state={isSelected ? 'selected' : 'enabled'}
          onPress={() => {
            handleChangeFilter(item);
          }}
        >
          {text}
        </Chip>
      );
    }

    const selectedTextColor = radioFilter.render?.selected_text_color ?? Colors.content_primary;
    const textColor = radioFilter.render?.text_color ?? Colors.content_secondary;
    return (
      <Flex
        testID={testID}
        backgroundColorValue={radioFilter.render?.background_color}
        borderBottomWidth={!isSecondaryTab && isSelected ? 2 : 0}
        padding={{ horizontal: isSecondaryTab ? 4 : 2, vertical: 13 }}
        onPress={() => {
          handleChangeFilter(item);
        }}
      >
        <Typography
          type={isSecondaryTab || isSelected ? 'subtitle2' : 'body2'}
          rawColor={isSelected ? selectedTextColor : textColor}
        >
          {text}
        </Typography>
      </Flex>
    );
  };

  return (
    <AnimatedView>
      <AnimatedList
        ref={radioListRef}
        horizontal={true}
        onScrollToIndexFailed={onScrollToIndexFailed}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={contentContainerStyle}
        data={radioFilter.possible_items}
        extraData={radioFilter.selected_value}
        renderItem={renderItem}
        onContentSizeChange={onContentSizeChange}
      />
    </AnimatedView>
  );
};
